/**
 * Route guard.
 *
 * Fails closed, unlike the component it replaces — AppAccessGate treated an
 * in-flight or failed check as "granted".
 *
 * This is presentation. The API enforces the same permission and returns 403
 * regardless of what the browser decides to render.
 */
import { ShieldProhibited24Regular } from '@fluentui/react-icons';
import {
  Body1,
  Caption1,
  Card,
  Spinner,
  Subtitle2,
  makeStyles,
  tokens,
} from '@pacxa/ui';
import type { ReactNode } from 'react';

import { usePrincipal } from './PrincipalProvider.js';

const useStyles = makeStyles({
  centred: {
    display: 'flex',
    height: '384px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    height: '384px',
    alignItems: 'center',
    justifyContent: 'center',
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    rowGap: tokens.spacingVerticalS,
    maxWidth: '448px',
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    textAlign: 'center',
  },
  icon: {
    fontSize: '48px',
    width: '48px',
    height: '48px',
    color: tokens.colorPaletteRedForeground1,
    marginBottom: tokens.spacingVerticalS,
  },
  body: {
    color: tokens.colorNeutralForeground2,
  },
  detail: {
    fontFamily: tokens.fontFamilyMonospace,
    color: tokens.colorNeutralForeground3,
    marginTop: tokens.spacingVerticalS,
  },
});

export function RequirePermission({
  permission,
  children,
}: {
  permission: string;
  children: ReactNode;
}) {
  const styles = useStyles();
  const { can, isLoading, error } = usePrincipal();

  if (isLoading) {
    return (
      <div className={styles.centred} role="status" aria-live="polite">
        <Spinner label="Checking access" labelPosition="below" />
      </div>
    );
  }

  if (error) {
    return (
      <AccessMessage
        title="Could not verify access"
        body="We couldn't load your permissions. Reload the page, and if it keeps happening contact IT."
      />
    );
  }

  if (!can(permission)) {
    return (
      <AccessMessage
        title="Access denied"
        body="You don't have permission to view this. Ask an ERP administrator to grant it — they can do this from Administration → Access."
        detail={permission}
      />
    );
  }

  return <>{children}</>;
}

function AccessMessage({
  title,
  body,
  detail,
}: {
  title: string;
  body: string;
  detail?: string;
}) {
  const styles = useStyles();
  return (
    <Card className={styles.card}>
      <div className={styles.message}>
        <ShieldProhibited24Regular className={styles.icon} aria-hidden />
        <Subtitle2 as="h3">{title}</Subtitle2>
        <Body1 className={styles.body}>{body}</Body1>
        {detail ? <Caption1 className={styles.detail}>Requires: {detail}</Caption1> : null}
      </div>
    </Card>
  );
}
